import { useEffect } from 'react';
import { ActionType, TextKey } from '@mews-commander/enums';
import { WithAvailabilityBlockId, WithGuideLink } from '@mews-commander/types';
import { ModalBodyWrapper, ModalWrapper, useEnterpriseBuildLocation } from '@mews-commander/ui';
import { IconName } from '@optimus-web/core';
import { LocalizedText } from '@mews/framework';
import { AvailabilityBlock, AvailabilityBlockState } from '@mews-commander/data-access';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { useCloseButton } from '../../../core/windowManager/hooks';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { HeadingAction } from '../../../legacy/React/Components/HeadingAction/Action/types';
import { useAvailabilityBlockStateActions } from '../components/Header/utils/useAvailabilityBlockStateActions';
import { useAvailabilityBlock } from '../utils/useAvailabilityBlock';
import { AvailabilityBlock as AvailabilityBlockComponent } from '../components';
import { changeDocumentTitle } from '../../../middleware';
import { showAvailabilityBlockActionLog } from '../actions/availabilityBlock';
import { useIsAvailabilityBlockEditable } from '../utils/useIsAvailabilityBlockEditable';

type AvailabilityBlockDashboardProps = WithAvailabilityBlockId & WithGuideLink;

export const AvailabilityBlockDashboard: React.FC<AvailabilityBlockDashboardProps> = ({
    availabilityBlockId,
    ...other
}) => {
    const dispatch = useCommanderThunkDispatch();
    const availabilityBlock = useAvailabilityBlock(availabilityBlockId);

    const availabilityBlockName = availabilityBlock?.name;
    useEffect(() => {
        if (availabilityBlockName) {
            dispatch(changeDocumentTitle(availabilityBlockName));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availabilityBlockName]);

    if (!availabilityBlock) {
        return null;
    }

    return <AvailabilityBlockDashboardBody {...other} availabilityBlock={availabilityBlock} />;
};

interface AvailabilityBlockDashboardBodyProps {
    availabilityBlock: AvailabilityBlock;
    guideLink: string;
}

const AvailabilityBlockDashboardBody: React.FC<AvailabilityBlockDashboardBodyProps> = ({
    availabilityBlock,
    guideLink,
}) => {
    const dispatch = useCommanderThunkDispatch();
    const isEditable = useIsAvailabilityBlockEditable();

    const buildLocation = useEnterpriseBuildLocation();
    const breadcrumbUrl = buildLocation({ to: 'AvailabilityBlockReport/Index' }).href;

    const breadcrumbs: Breadcrumbs = [
        { text: <LocalizedText textKey={TextKey.AvailabilityBlockPlural} />, url: breadcrumbUrl },
        { text: availabilityBlock.name, active: true },
    ];

    const closeButton = useCloseButton();

    const { cancel, dialog } = useAvailabilityBlockStateActions(availabilityBlock);

    const openAvailabilityBlockActionLog = () =>
        dispatch(showAvailabilityBlockActionLog({ availabilityBlockId: availabilityBlock.id }));

    const detailUrl = buildLocation({
        to: 'AvailabilityBlock/Detail/$availabilityBlockId',
        params: { availabilityBlockId: availabilityBlock.id },
        search: { OwnerId: availabilityBlock.serviceId },
    }).href;

    const actions: HeadingAction[] = [
        ...(isEditable(availabilityBlock)
            ? ([
                  {
                      url: detailUrl,
                      icon: IconName.Edit,
                      text: <LocalizedText textKey={TextKey.Edit} />,
                      type: ActionType.Link,
                  },
              ] as HeadingAction[])
            : []),
        ...(availabilityBlock.state !== AvailabilityBlockState.Canceled
            ? ([
                  {
                      icon: IconName.CancelSquare,
                      text: <LocalizedText textKey={TextKey.Cancel} />,
                      'data-test-icon-button': IconName.CancelSquare,
                      type: ActionType.Link,
                      onClick: cancel,
                  },
              ] as HeadingAction[])
            : []),
        {
            icon: IconName.ActionLog,
            text: <LocalizedText textKey={TextKey.ActionLog} />,
            type: ActionType.Custom,
            onClick: openAvailabilityBlockActionLog,
        },
        {
            url: guideLink,
            icon: IconName.Help,
            text: <LocalizedText textKey={TextKey.Help} />,
            type: ActionType.Link,
            target: '_blank',
        },
    ];

    return (
        <>
            {dialog}
            <ModalWrapper>
                <ModalHeader breadcrumbs={breadcrumbs} actions={actions} primaryAction={closeButton} />
                <ModalBodyWrapper padded>
                    <AvailabilityBlockComponent availabilityBlockId={availabilityBlock.id} />
                </ModalBodyWrapper>
            </ModalWrapper>
        </>
    );
};
